import * as React from 'react';
import { 
  Image,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  useWindowDimensions,
  View,
} from 'react-native';
import * as AppUtilities from "../context/AppUtilities";
import * as AppConstants from "../context/AppConstants";
import * as AppState from "../context/AppState";
import { AppContext } from "../context/AppContext";
import { useSelector, useDispatch } from 'react-redux';

const styles = StyleSheet.create({
  containerFloating: {
    position: "absolute",
    width: "100%",
    top: 10,
  },
  containerNonFloating: {
    width: "100%",
  },
  searchFloating: {
    width: "100%",
    flexDirection: "row",
    alignItems: "center",
    borderWidth: 1,
    borderColor: "#777777",
    borderRadius: 10,
    height: 40,
    paddingLeft: 10,
    paddingRight: 10,
    backgroundColor: "#ffffff",
    opacity: 0.8
  },
  searchNormal: {
    width: "100%",
    flexDirection: "row",
    alignItems: "center",
    borderWidth: 1,
    borderColor: "#777777",
    borderRadius: 10,
    height: 40,
    padding: 10,
    backgroundColor: "#ffffff",
  },
  searchWithQuickLinks: {
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
    borderBottomWidth: 0,
  },
  textBoxNormal: {
    paddingLeft: 10,
    color: "#777777",
    flex: 1,
    ...Platform.select({ web: {
      cursor: 'text',
    }}),
  },
  textBoxFloating: {
    paddingLeft: 10,
    color: "#222222",
    flex: 1,
    ...Platform.select({ web: {
      cursor: 'text',
    }}),
  },
  quickLinksFloating: {
    width: "100%",
    backgroundColor: "#ffffff",
    opacity: 0.8,
    padding: 5,
    borderWidth: 1,
    borderColor: "#777777",
    borderBottomLeftRadius: 10,
    borderBottomRightRadius: 10,
    paddingLeft: 14,
  },
  quickLinksNormal: {
    width: "100%",
    backgroundColor: "#ffffff",
    padding: 5,
    borderWidth: 1,
    borderColor: "#777777",
    borderBottomLeftRadius: 10,
    borderBottomRightRadius: 10,
    paddingLeft: 14,
  },
  quickLinksContentContainer: {
    justifyContent: "center",
  },
  quickLinkButton: {
    paddingRight: 20,
    flexDirection: "row",
    alignItems: "center",
  },
  quickLinkIcon: {
    marginRight: 5,
    width: 24,
    height: 24,
  },
  quickLinkText: {
    color: "#222222",
    fontSize: 12,
  },
  quickLinkTextSelected: {
    color: "#222222",
    fontSize: 12,
    fontWeight: "bold",
  },
});

function doesSearchInclude(search, q) {
  return search && search.toLowerCase().includes(q.toLowerCase());
}

const MORE_FILTERS = "More Filters";

// When adding here, also consider adding to ALLOW_SUBSTRING_SEARCHES in utilities.rb
const SEARCHES = [
  ["Grocery Stores", require('../../assets/cart-grey.png')],
  ["Dining", require('../../assets/restaurant-grey.png')],
  // Not enough coffee shops right now
  //["Coffee Shops", require('../../assets/coffee-grey.png')],
  ["Farmers Markets", require('../../assets/farmers-market-booth.png')],
  ["Regenerative", require('../../assets/agriculture-grey.png')],
  [MORE_FILTERS, require('../../assets/filter-circle-grey.png')],
];

export function SearchLauncher({
                  floating,
                  floatPaddingLeft,
                  floatPaddingRight,
                  hideSearchLocation,
                  showQuickLinks,
                }) {
  const { showSearch } = React.useContext(AppContext);
  const { width } = useWindowDimensions();
  const dataFilter = useSelector((state) => state.dataFilter);
  const globalDispatch = useDispatch();
  const generalSearch = useSelector((state) => state.dataFilter[AppConstants.SYNTHESIZED_TYPES.GENERAL_SEARCH[AppConstants.TYPE_KEYS.URL_KEY]]);
  const producer = useSelector((state) => state.dataFilter[AppConstants.SYNTHESIZED_TYPES.PRODUCER[AppConstants.TYPE_KEYS.URL_KEY]]);

  const onClear = React.useCallback(() => {
    let newFilter = Object.assign({}, dataFilter);
    delete newFilter[AppConstants.SYNTHESIZED_TYPES.GENERAL_SEARCH[AppConstants.TYPE_KEYS.URL_KEY]];
    delete newFilter[AppConstants.SYNTHESIZED_TYPES.PRODUCER[AppConstants.TYPE_KEYS.URL_KEY]];
    globalDispatch(AppState.createDispatch(AppConstants.DISPATCH.UPDATE_DATA_FILTER, newFilter));
  }, [dataFilter]);

  // The last number is effectively the minimum size (e.g. on web)
  const maxWidth = Math.max(width - floatPaddingLeft - floatPaddingRight, 200);
  let textboxStyle = null;
  if (floating && showQuickLinks) {
    textboxStyle = [ styles.searchFloating, styles.searchWithQuickLinks, { maxWidth: maxWidth }];
  } else if (floating) {
    textboxStyle = [ styles.searchFloating, { maxWidth: maxWidth }];
  } else if (showQuickLinks) {
    textboxStyle = [ styles.searchNormal, styles.searchWithQuickLinks ];
  } else {
    textboxStyle = styles.searchNormal;
  }

  const renderButton = React.useCallback((q, icon) => {
    return (
      <Pressable
        key={q}
        style={styles.quickLinkButton}
        onPress={() => {
          if (q == MORE_FILTERS) {
            showSearch(hideSearchLocation);
          } else {
            const newFilter = Object.assign({}, dataFilter);

            // If search already includes this button text then that
            // means we're toggling it off
            if (doesSearchInclude(generalSearch, q)) {
              let newSearch = generalSearch.replace(new RegExp(q, "i"), "");
              if (newSearch && newSearch.length > 0) {
                newFilter[AppConstants.SYNTHESIZED_TYPES.GENERAL_SEARCH[AppConstants.TYPE_KEYS.URL_KEY]] = newSearch.trim();
              } else {
                delete newFilter[AppConstants.SYNTHESIZED_TYPES.GENERAL_SEARCH[AppConstants.TYPE_KEYS.URL_KEY]];
              }
            } else {
              // The search isn't already there, so we want to add it.
              // If one of the other button's texts are already there,
              // then we want to remove that. Then we'll always append our new search.
              let newSearch = generalSearch;
              for (let i = 0; i < SEARCHES.length; i++) {
                if (doesSearchInclude(generalSearch, SEARCHES[i][0])) {
                  newSearch = newSearch.replace(new RegExp(SEARCHES[i][0], "i"), "");
                }
              }

              if (newSearch && newSearch.length > 0) {
                newSearch = newSearch.trim() + " " + q;
              } else {
                newSearch = q;
              }
              newFilter[AppConstants.SYNTHESIZED_TYPES.GENERAL_SEARCH[AppConstants.TYPE_KEYS.URL_KEY]] = newSearch;
            }
            globalDispatch(AppState.createDispatch(AppConstants.DISPATCH.UPDATE_DATA_FILTER, newFilter));
          }
        }}
      >
        <Image source={icon} style={styles.quickLinkIcon} />
        <Text style={generalSearch && doesSearchInclude(generalSearch, q) ? styles.quickLinkTextSelected : styles.quickLinkText}>{q}</Text>
      </Pressable>
    );
  }, [dataFilter, generalSearch]);

  let finalText = null;
  if (!AppUtilities.isBlank(generalSearch)) {
    finalText = generalSearch;
  }
  if (!AppUtilities.isBlank(producer)) {
    if (finalText) {
      finalText += " producer:";
    } else {
      finalText = "";
    }
    finalText += producer;
  }

  return (
    <View
      style={
        floating ?
            [
              styles.containerFloating,
              {
                maxWidth: maxWidth,
              }
            ]
          :
            styles.containerNonFloating
      }
    >
      <Pressable
        style={textboxStyle}
        onPress={() => showSearch(hideSearchLocation)}
      >
        <Image source={require('../../assets/magnify-grey-25.png')} style={AppConstants.STYLES.image24x24} />
        <Text style={floating ? styles.textBoxFloating : styles.textBoxNormal} selectable={false}>
          {
            finalText ? finalText : "Search"
          }
        </Text>
        {
          finalText ?
              <Pressable onPress={onClear} hitSlop={20}>
                <Image source={require('../../assets/clear-grey-18.png')} style={AppConstants.STYLES.image18x18} />
              </Pressable>
            :
              <Image source={require('../../assets/filter-circle-grey.png')} style={AppConstants.STYLES.image24x24} />
          }
      </Pressable>
      {
        showQuickLinks ?
            <ScrollView
              style={
                floating ?
                    [
                      styles.quickLinksFloating,
                      {
                        maxWidth: maxWidth,
                      }
                    ]
                  :
                    styles.quickLinksNormal
              }
              //contentContainerStyle={styles.quickLinksContentContainer}
              horizontal={true}
            >
              {SEARCHES.map(searchItem => renderButton(searchItem[0], searchItem[1]))}
            </ScrollView>
          :
            null
      }
    </View>
  );
}
